import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Check, 
  X, 
  Edit3, 
  Trash2, 
  Search,
  Filter,
  List,
  CheckCircle2,
  Circle,
  MoreVertical,
  RotateCcw,
  CheckSquare,
  Square,
  Sparkles
} from 'lucide-react';
import { apiService } from '../services/apiService';
import realtimeService from '../services/realtimeService';
import AddItemModal from './AddItemModal';
import RecentItemsModal from './RecentItemsModal';
import SmartSeedModal from './SmartSeedModal';
import QuickAddSection from './QuickAddSection';
import ConfirmModal from './ConfirmModal';
import { useLanguage } from '../contexts/LanguageContext';
import { useFontSize } from '../contexts/FontSizeContext';
import { useDisplay } from '../contexts/DisplayContext';

const ShoppingListView = ({ list, onBack, onUpdateList, onDeleteList }) => {
  const [items, setItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, purchased
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showRecentItemsModal, setShowRecentItemsModal] = useState(false);
  const [showSmartSeedModal, setShowSmartSeedModal] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(true); // Start collapsed for mobile-first
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [showClearCheckedModal, setShowClearCheckedModal] = useState(false);
  const { t, getAllCategories } = useLanguage();
  const { fontSize, updateFontSize, getFontSizes, getFontSizeLabel } = useFontSize();
  const { getListCategorySetting } = useDisplay();

  console.log('ShoppingListView: showOptionsMenu =', showOptionsMenu);
  console.log('ShoppingListView: component rendered');
  console.log('🔧 Real-time status:', {
    isConnected: realtimeConnected,
    connectionAttempted,
    currentListId: realtimeService.currentListId,
    expectedListId: list.id,
    serviceStatus: realtimeService.getConnectionStatus()
  });

  useEffect(() => {
    console.log('🔧 ShoppingListView: Setting up for list:', list.id, typeof list.id);
    loadListDetails();
    
    // Connect to real-time service and join this list
    realtimeService.connect();
    realtimeService.joinList(list.id);

    // Start with connection as false, will be updated by event handlers
    setRealtimeConnected(false);
    console.log('🔧 Real-time connection: Starting connection process...');

    // Set up real-time event listeners
    const handleItemAdded = (data) => {
      console.log('📝 Real-time: Item added event received:', data);
      console.log('📝 Real-time: Current list ID:', list.id, 'Event list ID:', data.listId);
      console.log('📝 Real-time: Type comparison:', typeof list.id, typeof data.listId);
      console.log('📝 Real-time: Strict equality:', list.id === data.listId);
      console.log('📝 Real-time: Loose equality:', list.id == data.listId);
      
      if (data.listId == list.id) { // Use == for type coercion
        console.log('📝 Real-time: List IDs match, processing item addition');
        console.log('📝 Real-time: Item to add:', data.item);
        
        setItems(prev => {
          console.log('📝 Real-time: Current items count:', prev.length);
          // Check if item already exists to avoid duplicates
          const exists = prev.find(item => item.id === data.item.id);
          if (exists) {
            console.log('📝 Real-time: Item already exists, skipping:', data.item.id);
            return prev;
          }
          console.log('📝 Real-time: Adding new item to UI:', data.item);
          const newItems = [...prev, data.item];
          console.log('📝 Real-time: New items count:', newItems.length);
          return newItems;
        });
        // Refresh stats
        refreshStats();
      } else {
        console.log('📝 Real-time: Item added for different list:', data.listId, 'vs', list.id);
      }
    };

    const handleItemUpdated = (data) => {
      console.log('✏️ Real-time: Item updated event received:', data);
      console.log('✏️ Current list ID:', list.id, 'Event list ID:', data.listId);
      if (data.listId == list.id) { // Use == for type coercion
        console.log('✏️ Updating item in state:', data.item.id, 'checked:', data.item.checked);
        setItems(prev => {
          const updated = prev.map(item => 
            item.id === data.item.id ? { ...item, ...data.item } : item
          );
          console.log('✏️ Items state before update:', prev.find(i => i.id === data.item.id)?.checked);
          console.log('✏️ Items state after update:', updated.find(i => i.id === data.item.id)?.checked);
          return updated;
        });
        // Refresh stats
        refreshStats();
      } else {
        console.log('✏️ Real-time: Item updated for different list:', data.listId, 'vs', list.id);
      }
    };

    const handleItemDeleted = (data) => {
      console.log('🗑️ Real-time: Item deleted event received:', data);
      if (data.listId == list.id) { // Use == for type coercion
        setItems(prev => prev.filter(item => item.id !== data.itemId));
        // Refresh stats
        refreshStats();
      }
    };

    const handleListCleared = (data) => {
      if (data.listId == list.id) { // Use == for type coercion
        if (data.type === 'all') {
          setItems([]);
        } else if (data.type === 'checked') {
          setItems(prev => prev.filter(item => !item.checked));
        }
        // Refresh stats
        refreshStats();
      }
    };

    const handleListUnchecked = (data) => {
      if (data.listId == list.id) { // Use == for type coercion
        setItems(data.items);
        // Refresh stats
        refreshStats();
      }
    };

    // Connection status handlers
    const handleConnectionChange = (connected) => {
      console.log('🔧 Real-time connection status changed:', connected);
      setRealtimeConnected(connected);
      setConnectionAttempted(true);
    };

    const handleNeedRefresh = (data) => {
      console.log('🔄 Refreshing list due to:', data.reason);
      loadListDetails();
    };

    // List blocking handler
    const handleListBlocked = (data) => {
      const { listId, isBlocked, listName } = data;
      // If this list is blocked, redirect back to list manager
      if (isBlocked && parseInt(listId) === list.id) {
        console.log(`🚫 Current list "${listName}" was blocked, redirecting to list manager`);
        onBack();
      }
    };

    // Add timeout to check connection status after a delay
    const connectionCheckTimeout = setTimeout(() => {
      const status = realtimeService.getConnectionStatus();
      console.log('🔧 Fallback connection check:', status);
      setRealtimeConnected(status.isConnected);
      setConnectionAttempted(true);
    }, 2000); // Check after 2 seconds

    realtimeService.on('item-added', handleItemAdded);
    realtimeService.on('item-updated', handleItemUpdated);
    realtimeService.on('item-deleted', handleItemDeleted);
    realtimeService.on('list-cleared', handleListCleared);
    realtimeService.on('list-unchecked', handleListUnchecked);
    realtimeService.on('connection-changed', handleConnectionChange);
    realtimeService.on('list-blocked', handleListBlocked);
    realtimeService.on('need-refresh', handleNeedRefresh);

    // Cleanup on unmount
    return () => {
      clearTimeout(connectionCheckTimeout);
      realtimeService.off('item-added', handleItemAdded);
      realtimeService.off('item-updated', handleItemUpdated);
      realtimeService.off('item-deleted', handleItemDeleted);
      realtimeService.off('list-cleared', handleListCleared);
      realtimeService.off('list-unchecked', handleListUnchecked);
      realtimeService.off('connection-changed', handleConnectionChange);
      realtimeService.off('list-blocked', handleListBlocked);
      realtimeService.off('need-refresh', handleNeedRefresh);
      realtimeService.leaveList(list.id);
    };
  }, [list.id]);

  useEffect(() => {
    // Close options menu when clicking outside
    const handleClickOutside = (event) => {
      if (showOptionsMenu && !event.target.closest('.list-options')) {
        setShowOptionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu]);

  const loadListDetails = async () => {
    try {
      setLoading(true);
      const [listData, statsData] = await Promise.all([
        apiService.getList(list.id),
        apiService.getListStats(list.id)
      ]);
      setItems(listData.items || []);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading list details:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = async () => {
    try {
      const statsData = await apiService.getListStats(list.id);
      setStats(statsData);
    } catch (err) {
      console.error('Error refreshing stats:', err);
    }
  };

  const handleAddItem = async (itemData) => {
    try {
      console.log('🚀 Adding item:', itemData);
      
      // Check for existing item with same name (case-insensitive)
      const existingItem = items.find(item => 
        item.name.toLowerCase().trim() === itemData.name.toLowerCase().trim()
      );

      let result;
      if (existingItem) {
        console.log('📝 Found existing item:', existingItem);
        if (existingItem.checked) {
          // Item exists and is completed - uncheck it and set quantity to 1
          console.log('📝 Reactivating purchased item');
          const updatedItem = await apiService.updateItem(list.id, existingItem.id, { 
            checked: false,
            quantity: 1
          });
          console.log('📝 Item reactivated:', updatedItem);
          // The real-time update will handle the UI update
          result = { 
            type: 'reactivated', 
            item: updatedItem,
            message: 'Item unchecked and ready to shop again'
          };
        } else {
          // Item exists and is not completed - add to quantity as before
          console.log('📝 Updating quantity for existing item');
          const currentQuantity = parseInt(existingItem.quantity, 10) || 1;
          const addQuantity = parseInt(itemData.quantity, 10) || 1;
          const newQuantity = currentQuantity + addQuantity;
          console.log('📝 Quantity calculation - Current:', currentQuantity, 'Adding:', addQuantity, 'New total:', newQuantity);
          const updatedItem = await apiService.updateItem(list.id, existingItem.id, { 
            quantity: newQuantity 
          });
          console.log('📝 Item quantity updated:', updatedItem);
          // The real-time update will handle the UI update
          result = { 
            type: 'updated', 
            item: updatedItem, 
            addedQuantity: itemData.quantity,
            newQuantity: newQuantity
          };
        }
      } else {
        console.log('📝 Creating new item');
        // Check if we have a learned category for this item
        // TEMPORARILY DISABLED to debug 500 error
        /*
        if (!itemData.category || itemData.category === 'General') {
          try {
            const learnedData = await apiService.getLearnedCategory(itemData.name);
            if (learnedData.category) {
              itemData.category = learnedData.category;
            }
          } catch (err) {
            // Ignore learning errors, just use the provided category
            console.log('Could not get learned category:', err);
          }
        }
        */
        
        // Item doesn't exist, create new one
        const newItem = await apiService.addItem(list.id, itemData);
        console.log('📝 New item created:', newItem);
        // The real-time update will handle the UI update
        result = { type: 'added', item: newItem };
      }
      
      console.log('📝 Add item result:', result);
      
      // If real-time is not connected, manually refresh the list
      if (!realtimeConnected) {
        console.log('📝 Real-time not connected, manually refreshing list');
        setTimeout(() => {
          loadListDetails();
        }, 500); // Small delay to ensure server has processed the request
      }
      
      // Stats will be refreshed by real-time event handler or manual refresh
      return result;
    } catch (err) {
      console.error('Error adding item:', err);
      throw err;
    }
  };

  const handleSmartSeedComplete = (result) => {
    console.log('✨ Smart seed completed, result:', result);
    
    // If categories were added, refresh the page to reload user data
    if (result && result.categoriesAdded > 0) {
      console.log('🌱 Categories were added, refreshing page to reload user data...');
      // Force a page refresh to reload user data with new categories
      window.location.reload();
    } else {
      // Just close the modal and refresh list if no categories were added
      setShowSmartSeedModal(false);
      loadListDetails();
    }
  };

  const handleToggleItem = async (itemId, checked) => {
    try {
      console.log('🔄 Toggle item:', itemId, 'to checked:', checked, 'real-time connected:', realtimeConnected);
      const updatedItem = await apiService.updateItem(list.id, itemId, { checked });
      console.log('✅ API response for toggle:', updatedItem);
      
      // Always refresh stats when an item is completed (checked)
      if (checked) {
        console.log('📊 Item completed, refreshing stats...');
        refreshStats();
      }
      
      // If real-time is not connected, manually update the item in state
      if (!realtimeConnected) {
        console.log('📝 Real-time not connected, manually updating item in state');
        setItems(prev => prev.map(item => 
          item.id === itemId ? { ...item, checked } : item
        ));
        refreshStats();
      } else {
        console.log('📝 Real-time connected, waiting for real-time update...');
      }
      
      // The real-time update will handle the UI update if connected
    } catch (err) {
      console.error('Error updating item:', err);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await apiService.deleteItem(list.id, itemId);
      
      // If real-time is not connected, manually remove the item from state
      if (!realtimeConnected) {
        console.log('📝 Real-time not connected, manually removing item from state');
        setItems(prev => prev.filter(item => item.id !== itemId));
        refreshStats();
      }
      
      // The real-time update will handle the UI update if connected
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleClearAllItems = async () => {
    if (window.confirm(t('confirmClearAll'))) {
      try {
        await apiService.clearAllItems(list.id);
        // The real-time update will handle the UI update
        alert(t('itemsClearedAll'));
      } catch (err) {
        console.error('Error clearing all items:', err);
      }
    }
  };

  const handleClearCheckedItems = async () => {
    setShowClearCheckedModal(true);
  };

  const confirmClearCheckedItems = async () => {
    try {
      await apiService.clearCheckedItems(list.id);
      // The real-time update will handle the UI update
      // No confirmation message shown after successful removal
    } catch (err) {
      console.error('Error clearing checked items:', err);
    }
  };

  const handleUncheckAllItems = async () => {
    if (window.confirm(t('confirmUncheckAll'))) {
      try {
        const response = await apiService.uncheckAllItems(list.id);
        // The real-time update will handle the UI update
        alert(t('itemsUnchecked'));
      } catch (err) {
        console.error('Error unchecking all items:', err);
      }
    }
  };

  const filteredItems = useMemo(() => {
    return items
      .filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             item.category.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (filter === 'pending') return matchesSearch && !item.checked;
        if (filter === 'purchased') return matchesSearch && item.checked;
        return matchesSearch;
      })
      .sort((a, b) => {
        // Sort by purchased status (pending first), then by name
        if (a.checked !== b.checked) {
          return a.checked ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      });
  }, [items, searchTerm, filter]);

  const groupedItems = useMemo(() => {
    return filteredItems.reduce((groups, item) => {
      const category = item.category || 'General';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
      return groups;
    }, {});
  }, [filteredItems]);

  return (
    <div className="shopping-list-view" style={{
      fontSize: getFontSizes().base
    }}>
      <header className={`list-header ${headerCollapsed ? 'collapsed' : 'expanded'}`}>
        {/* Always visible compact header */}
        <div className="header-compact">
          <button onClick={onBack} className="back-btn">
            <ArrowLeft size={20} />
          </button>
          
          <div className="list-title-compact">
            <h1>{list.name}</h1>
            {realtimeConnected && (
              <div className="realtime-indicator" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '10px',
                color: '#22c55e',
                marginTop: '2px'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  backgroundColor: '#22c55e',
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite'
                }}></div>
                Live
              </div>
            )}
          </div>

          <button 
            onClick={() => setHeaderCollapsed(!headerCollapsed)}
            className="header-toggle-btn"
            title={headerCollapsed ? t('expandHeader', 'Expand header') : t('collapseHeader', 'Collapse header')}
          >
            {headerCollapsed ? <Plus size={20} /> : <X size={20} />}
          </button>
        </div>

        {/* Expandable header content */}
        {!headerCollapsed && (
          <div className="header-expanded">
            <div className="header-top-expanded">
              {list.canWrite !== false && (
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="add-item-btn"
                >
                  <Plus size={20} />
                  {t('addItem')}
                </button>
              )}

              <button 
                onClick={() => setShowRecentItemsModal(true)}
                className="recent-items-btn"
                title={t('recentItems', 'Recent Items')}
              >
                📝 {t('recent', 'Recent')}
              </button>

              {list.canWrite !== false && (
                <button 
                  onClick={() => setShowSmartSeedModal(true)}
                  className="smart-seed-btn"
                  title="Add template items to this list"
                >
                  <Sparkles size={16} />
                  Smart Seed
                </button>
              )}

              <div className="list-options">
                {list.canWrite !== false && (
                  <>
                    <button 
                      onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                      className="options-btn"
                    >
                      <MoreVertical size={20} />
                    </button>
                    
                    {showOptionsMenu && (
                      <div className="options-menu">
                        <button 
                          onClick={() => {
                            handleUncheckAllItems();
                            setShowOptionsMenu(false);
                          }}
                          className="option-item"
                        >
                          <RotateCcw size={16} />
                          {t('uncheckAll')}
                        </button>
                        <button 
                          onClick={() => {
                            handleClearCheckedItems();
                            setShowOptionsMenu(false);
                          }}
                          className="option-item"
                        >
                          <CheckSquare size={16} />
                          {t('clearChecked')}
                        </button>
                        <button 
                          onClick={() => {
                            handleClearAllItems();
                            setShowOptionsMenu(false);
                          }}
                          className="option-item danger"
                        >
                          <Trash2 size={16} />
                          {t('clearAllItems')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {list.description && (
              <div className="list-description">
                <p>{list.description}</p>
              </div>
            )}

            <div className="list-stats">
              <div className="progress-container">
                <div className="progress-bar large">
                  <div 
                    className="progress-fill"
                    style={{ width: `${stats.progress || 0}%` }}
                  ></div>
                </div>
                <div className="stats-text">
                  <span>{stats.purchasedItems || 0} {t('of')} {stats.totalItems || 0} {t('itemsCompleted')}</span>
                  <span className="percentage">{stats.progress || 0}%</span>
                </div>
              </div>
            </div>

            <div className="list-controls">
              {/* Font Size Testing Controls - Mobile Only */}
              <div className="font-size-tester mobile-only" style={{ 
                background: '#f0f8ff', 
                padding: '8px', 
                borderRadius: '8px', 
                marginBottom: '12px',
                border: '2px solid #4285f4'
              }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px', color: '#1976d2' }}>
                  📱 Font Size: {getFontSizeLabel()}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => updateFontSize('small')}
                    style={{ 
                      padding: '4px 8px', 
                      fontSize: '12px',
                      backgroundColor: fontSize === 'small' ? '#4285f4' : '#e3f2fd',
                      color: fontSize === 'small' ? 'white' : '#1976d2',
                      border: '1px solid #4285f4',
                      borderRadius: '4px'
                    }}
                  >
                    Small
                  </button>
                  <button 
                    onClick={() => updateFontSize('medium')}
                    style={{ 
                      padding: '4px 8px', 
                      fontSize: '12px',
                      backgroundColor: fontSize === 'medium' ? '#4285f4' : '#e3f2fd',
                      color: fontSize === 'medium' ? 'white' : '#1976d2',
                      border: '1px solid #4285f4',
                      borderRadius: '4px'
                    }}
                  >
                    Medium
                  </button>
                  <button 
                    onClick={() => updateFontSize('large')}
                    style={{ 
                      padding: '4px 8px', 
                      fontSize: '12px',
                      backgroundColor: fontSize === 'large' ? '#4285f4' : '#e3f2fd',
                      color: fontSize === 'large' ? 'white' : '#1976d2',
                      border: '1px solid #4285f4',
                      borderRadius: '4px'
                    }}
                  >
                    Large
                  </button>
                  <button 
                    onClick={() => updateFontSize('xlarge')}
                    style={{ 
                      padding: '4px 8px', 
                      fontSize: '12px',
                      backgroundColor: fontSize === 'xlarge' ? '#4285f4' : '#e3f2fd',
                      color: fontSize === 'xlarge' ? 'white' : '#1976d2',
                      border: '1px solid #4285f4',
                      borderRadius: '4px'
                    }}
                  >
                    X-Large
                  </button>
                  <button 
                    onClick={() => updateFontSize('huge')}
                    style={{ 
                      padding: '4px 8px', 
                      fontSize: '12px',
                      backgroundColor: fontSize === 'huge' ? '#4285f4' : '#e3f2fd',
                      color: fontSize === 'huge' ? 'white' : '#1976d2',
                      border: '1px solid #4285f4',
                      borderRadius: '4px'
                    }}
                  >
                    Huge
                  </button>
                </div>
              </div>

              <div className="search-container">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder={t('searchItems')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="filter-tabs">
                <button 
                  className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  {t('all')} ({items.length})
                </button>
                <button 
                  className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
                  onClick={() => setFilter('pending')}
                >
                  {t('pending')} ({items.filter(i => !i.checked).length})
                </button>
                <button 
                  className={`filter-tab ${filter === 'purchased' ? 'active' : ''}`}
                  onClick={() => setFilter('purchased')}
                >
                  {t('bought')} ({items.filter(i => i.checked).length})
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Connection Status Indicator */}
      {!realtimeConnected && connectionAttempted && (
        <div className="connection-warning" style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          color: '#856404',
          padding: '8px 16px',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          ⚠️ Real-time updates unavailable. Changes will refresh automatically.
        </div>
      )}
      {!realtimeConnected && !connectionAttempted && (
        <div className="connection-info" style={{
          backgroundColor: '#e3f2fd',
          border: '1px solid #90caf9',
          color: '#1565c0',
          padding: '6px 16px',
          fontSize: '13px',
          textAlign: 'center'
        }}>
          🔄 Establishing real-time connection...
        </div>
      )}

      <QuickAddSection 
        listId={list.id}
        onAddItem={handleAddItem} 
        canWrite={list.canWrite !== false}
        itemsCount={items.length}
      />

      <main className="list-content">
        {loading ? (
          <div className="loading-items">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="loading-item">
                <div className="loading-shimmer"></div>
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="empty-items">
            <List size={48} className="empty-icon" />
            <h3>{t('noItemsFound')}</h3>
            <p>
              {searchTerm 
                ? `${t('noItemsMatch')} "${searchTerm}"`
                : t('addFirstItem')
              }
            </p>
            {!searchTerm && list.canWrite !== false && (
              <button 
                onClick={() => setShowAddModal(true)}
                className="add-first-item-btn"
              >
                <Plus size={20} />
                {t('addFirstItemBtn')}
              </button>
            )}
          </div>
        ) : (
          <div className="items-container">
            <div className="all-categories-card">
              {Object.entries(groupedItems)
                .sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB))
                .map(([category, categoryItems], categoryIndex) => {
                const allCategories = getAllCategories();
                const categoryDisplayName = allCategories[category] || category;
                const isLastCategory = categoryIndex === Object.entries(groupedItems).length - 1;
                const shouldShowCategories = getListCategorySetting(list.id);
                
                return (
                <div key={category} className="category-section">
                  {shouldShowCategories && (
                    <div className="category-section-header">
                      <h3 className="category-section-title" style={{
                        fontSize: getFontSizes().categoryTitle
                      }}>{categoryDisplayName}</h3>
                      <span className="category-section-count" style={{
                        fontSize: getFontSizes().categoryCount
                      }}>{categoryItems.filter(item => !item.checked).length}</span>
                    </div>
                  )}
                  <div className="category-section-items">
                    {categoryItems.map((item, index) => (
                      <div 
                        key={item.id} 
                        className={`category-item ${item.checked ? 'purchased' : ''} ${index === categoryItems.length - 1 && isLastCategory ? 'last-item' : ''}`}
                        onClick={() => handleToggleItem(item.id, !item.checked)}
                      >
                        <div className="item-toggle">
                          {item.checked ? (
                            <CheckCircle2 size={18} className="purchased-icon" />
                          ) : (
                            <Circle size={18} className="pending-icon" />
                          )}
                        </div>

                        <div className="item-content">
                          <div className="item-main">
                            <h4 className="item-name" style={{
                              fontSize: getFontSizes().itemName
                            }}>{item.name}</h4>
                            <span className="item-quantity" style={{
                              fontSize: getFontSizes().itemQuantity
                            }}>×{item.quantity}</span>
                          </div>
                          {item.notes && (
                            <p className="item-notes" style={{
                              fontSize: getFontSizes().itemNotes
                            }}>{item.notes}</p>
                          )}
                        </div>

                        {list.canWrite !== false && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteItem(item.id);
                            }}
                            className="item-delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {shouldShowCategories && !isLastCategory && <div className="category-divider"></div>}
                </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {showAddModal && (
        <AddItemModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddItem}
          existingItems={items}
        />
      )}

      {showRecentItemsModal && (
        <RecentItemsModal
          onClose={() => setShowRecentItemsModal(false)}
          listId={list.id}
          onAddToList={handleAddItem}
        />
      )}

      {showSmartSeedModal && (
        <SmartSeedModal
          isOpen={showSmartSeedModal}
          onClose={() => setShowSmartSeedModal(false)}
          onItemsSeeded={handleSmartSeedComplete}
        />
      )}

      <ConfirmModal
        isOpen={showClearCheckedModal}
        onClose={() => setShowClearCheckedModal(false)}
        onConfirm={confirmClearCheckedItems}
        title={t('confirm', 'Confirm')}
        message={t('confirmClearChecked', 'Are you sure you want to clear all checked items from this list?')}
        confirmText={t('clearChecked', 'Clear Checked Items')}
        cancelText={t('cancel', 'Cancel')}
        danger={true}
      />
    </div>
  );
};

export default ShoppingListView;
